// src/pages/users/UserDetailPage.js
import React, { useEffect, useState } from "react"
import {
  Row, Col, Card, CardHeader, CardBody, Spinner, Alert,
} from "reactstrap"
import { useNavigate, useParams, Link } from "react-router-dom"
import axios from "axios"
import { API } from "../../api/urls"
import "../../assets/css/page-common.css"
import BackButton from "../../components/Common/BackButton";
import { useAuth } from "../../hooks/useAuth"
import { StatusPill, PriorityPill, CATEGORY_META, fmtDateTime } from "../Support/ticketMeta"

const NAVY = "#221C5D"
const GREEN = "#71C30B"
const RED = "#dc3545"

const translateRole = (role = "") => {
  switch (role) {
    case "ROLE_SUPER_ADMIN": return "Süper Yönetici"
    case "ROLE_COMPANY_ADMIN": return "Firma Yöneticisi"
    case "ROLE_COMPANY_ACCOUNTANT": return "Firma Muhasebecisi"
    case "ROLE_COMPANY_SALESPERSON": return "Firma Plasiyer"
    case "ROLE_COMPANY_CASHIER": return "Firma Kasa Sorumlusu"
    case "ROLE_ACCOUNT_ADMIN": return "Cari/Bayi Yönetici"
    case "ROLE_ACCOUNT_ACCOUNTANT": return "Cari/Bayi Muhasebe"
    case "ROLE_USER": return "Kullanıcı"
    case "ROLE_MODERATOR": return "Moderatör"
    default: return role.replace(/^ROLE_/, "")
  }
}

const formatDateTime = (v) => {
  if (!v) return "-"
  try { return new Date(v).toLocaleString("tr-TR") } catch { return v }
}

const StatusBadge = ({ active, label }) => {
  const color = active ? GREEN : "#6c757d"
  const bg = active ? "rgba(113,195,11,.1)" : "rgba(108,117,125,.1)"
  return (
    <span
      className="badge border-0"
      style={{
        borderRadius: 999,
        padding: ".35rem .6rem",
        backgroundColor: bg,
        color,
        fontWeight: 600,
        fontSize: ".78rem",
        border: `1px solid ${color}20`,
      }}
    >
      {label}
    </span>
  )
}

const UserDetailPage = () => {
  const navigate = useNavigate()
  const { id } = useParams()

  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [user, setUser] = useState(null)

  // Bu kullanıcının destek talepleri. Liste ucu yalnız SUPER_ADMIN'e açık olduğu
  // için kart da yalnız o rolde çizilir; diğer rollerde istek hiç atılmaz.
  const { roles } = useAuth()
  const isSuperAdmin = (roles || []).includes("ROLE_SUPER_ADMIN")
  const [tickets, setTickets] = useState([])
  const [ticketsLoading, setTicketsLoading] = useState(false)
  const [ticketsError, setTicketsError] = useState(null)

  const isCompanyUser = (user?.roles || []).some(r =>
    ["ROLE_SUPER_ADMIN", "ROLE_COMPANY_ADMIN", "ROLE_COMPANY_ACCOUNTANT", "ROLE_COMPANY_SALESPERSON", "ROLE_COMPANY_CASHIER"].includes(r)
  )

  const fetchUser = async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await axios.get(API.admin_user(id), { headers: {} })
      if (data?.statusCode === 200) {
        setUser(data.data)
      } else {
        setError("Kullanıcı bilgileri getirilemedi")
      }
    } catch (err) {
      if (err.response?.status === 401 || err.response?.status === 403) {
        navigate("/login")
        return
      }
      setError(err.response?.data?.message || "Kullanıcı bilgileri alınırken hata oluştu")
    } finally {
      setLoading(false)
    }
  }

  const fetchTickets = async () => {
    setTicketsLoading(true)
    setTicketsError(null)
    try {
      const { data } = await axios.get(API.admin_tickets_list, {
        params: { page: 0, size: 10, createdByUserId: id, sortBy: "createdAt", sortDir: "desc" },
      })
      setTickets(data?.data?.content || [])
    } catch {
      // Talep listesi sayfanın ana bilgisi değil; hata sayfayı kilitlemez, kart uyarır.
      setTicketsError("Destek talepleri alınamadı")
    } finally {
      setTicketsLoading(false)
    }
  }

  useEffect(() => {
    document.title = "Kullanıcı Detayları | Tahsilat360"
    fetchUser()
  }, [id]) // eslint-disable-line

  useEffect(() => {
    if (isSuperAdmin && id) fetchTickets()
  }, [isSuperAdmin, id]) // eslint-disable-line

  if (loading) {
    return (
      <div className="page-content text-center p-5">
        <Spinner size="lg" color="primary" />
        <h6 className="mt-3 text-muted">Yükleniyor...</h6>
      </div>
    )
  }

  if (error) {
    return (
      <div className="page-content">
        <Alert color="danger" className="rounded-4">
          <i className="bx bx-error-circle me-2" />
          {error}
          <button className="btn btn-sm btn-outline-danger ms-2 btn-rounded" onClick={fetchUser}>
            Tekrar Dene
          </button>
        </Alert>
      </div>
    )
  }

  return (
    <div className="page-content page-common">
      <div className="container-fluid">

        {/* ── Page Header ── */}
        <div className="page-header d-flex justify-content-between align-items-center flex-wrap gap-2">
          <div className="d-flex align-items-center gap-3">
            <BackButton />
            <div>
              <h5 className="mb-0 fw-bold" style={{ color: "#1e293b" }}>
                {user?.username || "Kullanıcı"}
              </h5>
              <span className="text-muted" style={{ fontSize: ".82rem" }}>
                Kullanıcı Detay Sayfası
              </span>
            </div>
          </div>
          <div className="d-flex gap-2 flex-wrap">
            {!!user && (
              <Link
                to={`/users/${user.id}/edit`}
                className="btn btn-navy btn-rounded d-flex align-items-center gap-1 px-3"
                style={{ height: 34, fontSize: ".8rem" }}
              >
                <i className="bx bx-edit-alt" style={{ fontSize: 16 }} /> Düzenle
              </Link>
            )}
            {/* Ödeme geçmişi userId ile süzülür: bu kullanıcının geçtiği tahsilatlar. */}
            {!!user && (
              <Link
                to={`/payment-history?userId=${user.id}`}
                className="btn btn-rounded d-flex align-items-center gap-1 px-3"
                style={{ height: 34, fontSize: ".8rem", background: "#198754", borderColor: "#198754", color: "#fff" }}
              >
                <i className="bx bx-dollar-circle" style={{ fontSize: 16 }} /> Ödeme Geçmişi
              </Link>
            )}
          </div>
        </div>

        {/* ── Temel Bilgiler ── */}
        <Card className="detail-card mb-3">
          <CardHeader>
            <div className="section-title">
              <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                <i className="bx bx-user" />
              </div>
              Temel Bilgiler
            </div>
          </CardHeader>
          <CardBody>
            <Row>
              {[
                { label: "Ad Soyad", value: user?.username },
                { label: "E-posta", value: user?.email },
                { label: "Telefon", value: user?.phoneNumber },
                {
                  label: "Roller",
                  value: Array.isArray(user?.roles) && user.roles.length
                    ? user.roles.map(translateRole).join(", ")
                    : null,
                },
              ].map((f, i) => (
                <Col key={i} xs={6} md={3} className="info-field">
                  <div className="info-label">{f.label}</div>
                  <div className="info-value">{f.value || "-"}</div>
                </Col>
              ))}
            </Row>
          </CardBody>
        </Card>

        {/* ── İstatistik Kutucukları ── */}
        <div className="stat-chip-row mb-3">
          {[
            {
              label: "Hesap Durumu",
              value: user?.enabled ? "Aktif" : "Pasif",
              color: user?.enabled ? GREEN : "#6c757d",
              bg: user?.enabled ? "rgba(113,195,11,.1)" : "rgba(108,117,125,.1)",
              icon: user?.enabled ? "bx-check-circle" : "bx-x-circle",
            },
            {
              label: "2FA Durumu",
              value: user?.twoFaEnabled ? "Aktif" : "Kapalı",
              color: user?.twoFaEnabled ? GREEN : "#6c757d",
              bg: user?.twoFaEnabled ? "rgba(113,195,11,.1)" : "rgba(108,117,125,.1)",
              icon: "bx-shield-quarter",
            },
            {
              label: "Başarısız Giriş",
              value: user?.failedAttempts ?? 0,
              color: (user?.failedAttempts || 0) > 0 ? RED : GREEN,
              bg: (user?.failedAttempts || 0) > 0 ? "rgba(220,53,69,.1)" : "rgba(113,195,11,.1)",
              icon: "bx-lock-alt",
            },
            {
              label: "Kullanıcı Tipi",
              value: isCompanyUser ? "Firma" : "Cari/Bayi",
              color: NAVY,
              bg: "rgba(34,28,93,.1)",
              icon: isCompanyUser ? "bx-buildings" : "bx-store",
            },
          ].map((s, i) => (
            <div key={i} className="stat-chip">
              <div className="stat-icon" style={{ background: s.bg, color: s.color }}>
                <i className={`bx ${s.icon}`} />
              </div>
              <div className="stat-number" style={{ color: s.color }}>{s.value}</div>
              <div className="stat-label">{s.label}</div>
            </div>
          ))}
        </div>

        {/* ── Hesap Durumu & Firma/Cari Bilgileri ── */}
        <Row className="mb-3 g-3">
          <Col lg={6}>
            <Card className="detail-card h-100">
              <CardHeader>
                <div className="section-title">
                  <div className="section-icon" style={{ background: "rgba(113,195,11,.1)", color: GREEN }}>
                    <i className="bx bx-check-shield" />
                  </div>
                  Hesap Durumu
                </div>
              </CardHeader>
              <CardBody>
                <Row>
                  <Col sm={6} className="info-field">
                    <div className="info-label">Aktif</div>
                    <div className="mt-1">
                      <StatusBadge active={user?.enabled} label={user?.enabled ? "Evet" : "Hayır"} />
                    </div>
                  </Col>
                  <Col sm={6} className="info-field">
                    <div className="info-label">2FA Etkin</div>
                    <div className="mt-1">
                      <StatusBadge active={user?.twoFaEnabled} label={user?.twoFaEnabled ? "Evet" : "Hayır"} />
                    </div>
                  </Col>
                </Row>
                <div style={{ borderTop: "1px solid #f0f2f5", paddingTop: ".75rem", marginTop: ".5rem" }}>
                  <Row>
                    <Col sm={4} className="info-field">
                      <div className="info-label">Başarısız Giriş</div>
                      <div className="info-value" style={{
                        color: (user?.failedAttempts || 0) > 0 ? RED : "#1e293b",
                      }}>
                        {user?.failedAttempts ?? 0}
                      </div>
                    </Col>
                    <Col sm={4} className="info-field">
                      <div className="info-label">Oluşturulma</div>
                      <div className="info-value">{formatDateTime(user?.createdAt)}</div>
                    </Col>
                    <Col sm={4} className="info-field">
                      <div className="info-label">Güncellenme</div>
                      <div className="info-value">{formatDateTime(user?.updatedAt)}</div>
                    </Col>
                  </Row>
                </div>
              </CardBody>
            </Card>
          </Col>

          <Col lg={6}>
            <Card className="detail-card h-100">
              <CardHeader>
                <div className="section-title">
                  <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                    <i className="bx bx-buildings" />
                  </div>
                  Firma / Cari Bilgileri
                </div>
              </CardHeader>
              <CardBody>
                <Row>
                  {/* Bağlı olduğu firma. Cari kullanıcılarında da doludur; backend
                      firmayı cari üzerinden çözer. */}
                  <Col sm={6} className="info-field">
                    <div className="info-label">Bağlı Olduğu Firma</div>
                    <div className="info-value">
                      {user?.companyName
                        ? (user?.companyId
                            ? <Link to={`/companies/${user.companyId}`}>{user.companyName}</Link>
                            : user.companyName)
                        : "-"}
                    </div>
                  </Col>
                  <Col sm={6} className="info-field">
                    <div className="info-label">Firma Kodu</div>
                    <div className="info-value">{user?.companyCode || "-"}</div>
                  </Col>
                </Row>
                {!isCompanyUser && (
                  <Row>
                    <Col sm={6} className="info-field">
                      <div className="info-label">Bağlı Olduğu Cari/Bayi</div>
                      <div className="info-value">
                        {user?.accountName
                          ? (user?.accountId
                              ? <Link to={`/accounts/${user.accountId}`}>{user.accountName}</Link>
                              : user.accountName)
                          : "-"}
                      </div>
                    </Col>
                    <Col sm={6} className="info-field">
                      <div className="info-label">Cari/Bayi Kodu</div>
                      <div className="info-value">{user?.accountCode || "-"}</div>
                    </Col>
                  </Row>
                )}
                {user?.id && (
                  <div style={{ borderTop: "1px solid #f0f2f5", paddingTop: ".75rem", marginTop: ".5rem" }}>
                    <div className="info-field">
                      <div className="info-label">Kullanıcı ID</div>
                      <div className="info-value" style={{ fontFamily: "monospace", fontSize: ".85rem" }}>
                        {user.id}
                      </div>
                    </div>
                  </div>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>

        {/* ── Destek Talepleri ── */}
        {isSuperAdmin && (
          <Card className="detail-card mb-3">
            <CardHeader>
              <div className="d-flex align-items-center justify-content-between flex-wrap gap-2">
                <div className="section-title">
                  <div className="section-icon" style={{ background: "rgba(34,28,93,.1)", color: NAVY }}>
                    <i className="bx bx-support" />
                  </div>
                  Destek Talepleri
                </div>
                {tickets.length > 0 && (
                  <Link
                    to={`/admin/tickets?createdByUserId=${id}`}
                    className="btn btn-sm btn-light btn-rounded px-3"
                    style={{ fontSize: ".78rem" }}
                  >
                    Tümünü gör
                  </Link>
                )}
              </div>
            </CardHeader>
            <CardBody>
              {ticketsLoading ? (
                <div className="text-center py-3"><Spinner size="sm" color="primary" /></div>
              ) : ticketsError ? (
                <Alert color="warning" className="mb-0 rounded-3">{ticketsError}</Alert>
              ) : tickets.length === 0 ? (
                <div className="text-muted" style={{ fontSize: ".85rem" }}>
                  Bu kullanıcının destek talebi bulunmuyor.
                </div>
              ) : (
                <div className="table-responsive">
                  <table className="table table-hover align-middle mb-0">
                    <thead>
                      <tr style={{ fontSize: ".78rem" }}>
                        <th>Konu</th>
                        <th className="d-none d-md-table-cell">Kategori</th>
                        <th className="d-none d-md-table-cell">Son Yanıt</th>
                        <th className="d-none d-lg-table-cell">Oluşturma</th>
                        <th>Öncelik</th>
                        <th>Durum</th>
                      </tr>
                    </thead>
                    <tbody>
                      {tickets.map((t) => (
                        <tr
                          key={t.id}
                          style={{ cursor: "pointer", fontSize: ".84rem" }}
                          onClick={() => navigate(`/admin/tickets/${t.id}`)}
                        >
                          <td className="fw-semibold text-dark">{t.subject}</td>
                          <td className="d-none d-md-table-cell">{CATEGORY_META[t.category] || t.category}</td>
                          <td className="d-none d-md-table-cell">{fmtDateTime(t.lastReplyAt)}</td>
                          <td className="d-none d-lg-table-cell">{fmtDateTime(t.createdAt)}</td>
                          <td><PriorityPill priority={t.priority} /></td>
                          <td><StatusPill status={t.status} /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardBody>
          </Card>
        )}

      </div>
    </div>
  )
}

export default UserDetailPage
